import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../services/axios";
import { fetchAllPaginated } from "../../services/fetchPaginated";
import { Home, isHomeDetailLoaded } from "../../lib/Home";

interface HomesData extends Array<Home> {}

const mergeHome = (homes: Home[], home: Home): Home[] => {
  const updated = [...homes];
  const index = updated.findIndex((entry) => entry.id === home.id);
  if (index >= 0) {
    updated[index] = { ...updated[index], ...home };
  } else {
    updated.push(home);
  }
  return updated;
};

export const fetchHomes = createAsyncThunk("homes/fetchHomes", async () => {
  return await fetchAllPaginated<Home>("/api-v1/home/?page_size=100").catch(
    () => []
  );
});

export const fetchHome = createAsyncThunk(
  "homes/fetchHome",
  async (id: number) => {
    const response = await axios.get(`/api-v1/home/${id}/`);
    return response.data as Home;
  }
);

export const createHome = createAsyncThunk("homes/createHome", async (data) => {
  const response = await axios.post("/api-v1/home/", data);
  return response.data;
});

export const updateHome = createAsyncThunk(
  "devices/updateHome",
  async ({
    id,
    data,
    updated = false,
  }: {
    id: number;
    data: any;
    updated: boolean;
  }) => {
    if (updated) {
      return data;
    }
    const response = await axios.patch(`/api-v1/home/${id}/`, data);
    return response.data;
  }
);

export const deleteHome = createAsyncThunk(
  "devices/deleteHome",
  async (id: number) => {
    await axios.delete(`/api-v1/home/${id}/`);
    return id;
  }
);

const homesSlice = createSlice({
  name: "homes",
  initialState: null as HomesData | null,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchHomes.fulfilled, (state, { payload }) => {
      payload.sort((a: Home, b: Home) => a.id - b.id);
      if (!state) {
        return payload;
      }
      const byId = new Map(state.map((home: Home) => [home.id, home]));
      return payload.map((summary: Home) => {
        const existing = byId.get(summary.id);
        if (existing && isHomeDetailLoaded(existing)) {
          return { ...summary, ...existing };
        }
        return summary;
      });
    });
    builder.addCase(fetchHome.fulfilled, (state: any, { payload }) => {
      if (!state) {
        return [payload];
      }
      const merged = mergeHome(state, payload);
      merged.sort((a: Home, b: Home) => a.id - b.id);
      return merged;
    });
    builder.addCase(createHome.fulfilled, (state: any, { payload }) => {
      return [...state, payload];
    });
    builder.addCase(updateHome.fulfilled, (state: any, { payload }) => {
      const updated = [...state];
      const old = updated.find((e) => e.id === payload.id);
      if (old) {
        updated.splice(updated.indexOf(old), 1, payload);
      } else {
        return [...state, payload];
      }
      return updated;
    });
    builder.addCase(deleteHome.fulfilled, (state: any, { payload }) => {
      const updated = [...state];
      const old = updated.find((e) => e.id === payload);
      if (old) {
        updated.splice(updated.indexOf(old), 1);
      }
      return updated;
    });
  },
});

export const homesMethods = {
  fetchHomes,
  fetchHome,
  createHome,
  updateHome,
  deleteHome,
};

export default homesSlice.reducer;
